import { Badge, Tab, Tabs, Tooltip } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { find, isEmpty, last, map } from "lodash";
import moment, { Moment } from "moment-timezone";
import { useEffect, useState } from "react";
import { useUsuario } from "../../../providers/usuarioProvider";
import MesInternacional from "./mesInternacional";
import CadastrarPontoDePartidaInternacional from "./pontoDePartida";
import { onSnapshot, query, orderBy, limit } from "firebase/firestore";
import ImpostoInternacionalDb from "../../../db/impostoInternacionalDb";
import ResultadoInternacionalDb from "../../../db/resultadoInternacionalDb";
import PosicaoInternacionalDb from "../../../db/posicaoInternacionalDb";
import ImpostoInternacional from "../../../models/impostoInternacional";
import ResultadoInternacional from "../../../models/resultadoInternacional";
import PosicaoInternacional from "../../../models/posicaoInternacional";
import Usuario from "../../../models/usuario";
import UsuarioDb from "../../../db/usuarioDb";
import { useLocation, useNavigate } from "react-router-dom";

export default function TimelineInternacional() {
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const [impostos, setImpostos] = useState<ImpostoInternacional[]>([]);
  const [resultados, setResultados] = useState<ResultadoInternacional[]>([]);
  const [primeiraPosicao, setPrimeiraPosicao] = useState<PosicaoInternacional>();
  const [timeline, setTimeline] = useState<Moment[]>([]);
  const [usuario, setUsuario] = useState<Usuario>();
  const location = useLocation();
  const [mesSelecionado, setMesSelecionado] = useState("");

  const handleTabChange = (_: unknown, newValue: string) => {
    setMesSelecionado(newValue);
    navigate(`${location.pathname}#${newValue}`, { replace: true });
  };

  useEffect(() => {
    if (visualizado) {
      return onSnapshot(
        new UsuarioDb().doc({ usuarioId: visualizado }),
        (snapshot) => {
          setUsuario(snapshot.data());
        }
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        query(
          new ImpostoInternacionalDb().collection({
            usuarioId: visualizado,
            impostoId: "",
          }),
          orderBy("dataBase", "asc")
        ),
        (query) => {
          if (!query.empty) {
            setImpostos(map(query.docs, (doc) => doc.data()));
          } else {
            setImpostos([]);
          }
        }
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        query(
          new ResultadoInternacionalDb().collection({
            usuarioId: visualizado,
            resultadoId: "",
          }),
          orderBy("dataBase", "asc")
        ),
        (query) => {
          if (!query.empty) {
            setResultados(map(query.docs, (doc) => doc.data()));
          } else {
            setResultados([]);
          }
        }
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        query(
          new PosicaoInternacionalDb().collection({
            usuarioId: visualizado,
            posicaoId: "",
          }),
          orderBy("data", "asc"),
          limit(1)
        ),
        (query) => {
          if (!query.empty) setPrimeiraPosicao(query.docs[0].data());
          else setPrimeiraPosicao(undefined);
        }
      );
    }
  }, [visualizado]);

  useEffect(() => {
    // Mostra a timeline quando houver posição cadastrada
    if (!primeiraPosicao) {
      setTimeline([]);
      return;
    }

    // Data inicial: mês seguinte à primeira posição (ponto de partida é fim do mês anterior)
    // Ex: posição em 2023-12-31 → timeline começa em Janeiro/2024
    // Subtraímos 1 dia para garantir que pegamos o mês correto
    // (posições antigas podem ter virado dia 1 do mês seguinte devido a timezone UTC)
    const dataInicial = primeiraPosicao.data.clone().subtract(1, "day").add(1, "month").startOf("month");

    const hoje = moment.tz("America/Sao_Paulo");

    // Gerar todos os meses desde a data inicial até hoje
    let meses: Moment[] = [];
    let mesAtual = dataInicial.clone();

    while (mesAtual.isSameOrBefore(hoje, "month")) {
      meses.push(mesAtual.clone());
      mesAtual.add(1, "month");
    }

    setTimeline(meses);

    if (!!location.hash) {
      setMesSelecionado(location.hash.slice(1));
    } else if (meses.length > 0) {
      setMesSelecionado(last(meses)?.format("YYYY-MM") ?? "");
    }
  }, [primeiraPosicao]);

  // A aba Ponto de Partida fica sempre acessível: é o único lugar para ver e
  // editar a custódia inicial e o prejuízo acumulado. Antes ela sumia depois
  // de salvar, e contadores não achavam mais o formulário (feedback da Ana,
  // set/2026). Para mudar a DATA existe "Alterar ponto de partida
  // internacional" nas Preferências.
  //
  // Data do ponto de partida: a gravada no usuário ou, sem nada salvo, o
  // primeiro ponto possível pela Lei 14.754/2023. O tz é fixado em SP porque
  // o dia vira o id do doc de posição (YYYY-MM-DD) — em navegador fora do
  // Brasil, startOf("day") no tz local podia cair no dia errado.
  const dataPontoDePartida =
    usuario?.pontoDePartidaInternacional
      ?.clone()
      .tz("America/Sao_Paulo")
      .startOf("day") ??
    moment.tz("2023-12-31", "America/Sao_Paulo").startOf("day");

  // Sem meses na timeline, a única aba disponível é o Ponto de Partida —
  // seleciona direto (inclusive vindo do deep-link #ponto-de-partida do
  // alerta de cálculo)
  useEffect(() => {
    if (isEmpty(timeline)) {
      setMesSelecionado("ponto-de-partida");
    }
  }, [timeline]);

  return (
    <TabContext value={mesSelecionado}>
      <Tabs
        sx={(theme) => ({
          bgcolor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          // Aba de mês é NAVEGAÇÃO, não dado em coluna — Mabry, não mono
          // (mesma correção da timeline doméstica).
          "& .MuiTab-root": {
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: 0,
            textTransform: "capitalize",
            color: theme.palette.text.secondary,
          },
          // Aba ativa em accent-ink sobre o tint de chip accent — o accent
          // sólido como tinta reprova em WCAG sobre superfície escura.
          "& .Mui-selected": {
            bgcolor: theme.palette.statusChip.accent.bg,
            color: theme.palette.accentInk,
          },
          "& .MuiTabs-indicator": {
            bgcolor: theme.palette.accentInk,
          },
          "& .MuiTabScrollButton-root": {
            color: theme.palette.text.secondary,
          },
        })}
        value={mesSelecionado}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
      >
        <Tab
          sx={{
            padding: 1,
            minWidth: 140,
            opacity: 1,
            position: "relative",
          }}
          label="Ponto de Partida"
          value="ponto-de-partida"
        />
        {map(timeline, (mes) => {
          const impostoDoMes = find(impostos, (imposto) =>
            imposto.dataBase.isSame(mes, "month")
          );
          const resultadoDoMes = find(resultados, (resultado) =>
            resultado.dataBase.isSame(mes, "month")
          );

          const temDados = !!impostoDoMes || !!resultadoDoMes;
          const recalcular = !!usuario?.atualizadoAte.isBefore(
            mes.clone().endOf("month"),
            "day"
          );
          const mesAtual = mes.isSame(moment.tz("America/Sao_Paulo"), "month");

          let tooltip: string = "";
          let cor: "default" | "warning" | "info" | "success" = "default";

          if (!temDados) {
            tooltip = "Esse mês ainda não foi calculado.";
          } else if (recalcular) {
            tooltip =
              "Alguma coisa pode ter mudado. Recalcule esse mês para garantir que está atualizado.";
            cor = "warning";
          } else if (mesAtual) {
            tooltip =
              "Os cálculos estão atualizados, mas como o mês não acabou você está vendo a parcial do mês.";
            cor = "info";
          } else {
            tooltip = "Esse mês já foi calculado e está atualizado!";
            cor = "success";
          }

          return (
            <Tab
              key={mes.format("YYYY-MM")}
              sx={{
                padding: 1,
                minWidth: 120,
                opacity: 1,
                position: "relative",
              }}
              label={
                <Tooltip title={tooltip}>
                  <Badge
                    sx={{ "& .MuiBadge-badge": { top: -4 } }}
                    color={cor}
                    variant="dot"
                    invisible={!temDados}
                  >
                    {mes.format("MMMM/YY")}
                  </Badge>
                </Tooltip>
              }
              value={mes.format("YYYY-MM")}
            />
          );
        })}
      </Tabs>
      <TabPanel value="ponto-de-partida">
        <CadastrarPontoDePartidaInternacional dataBase={dataPontoDePartida} />
      </TabPanel>
      {map(timeline, (mes) => {
        const mesFormatado = mes.format("YYYY-MM");

        const impostoDoMes = find(impostos, (imposto) =>
          imposto.dataBase.format("YYYY-MM") === mesFormatado
        );

        const resultadoDoMes = find(resultados, (resultado) =>
          resultado.dataBase.format("YYYY-MM") === mesFormatado
        );

        return (
          <TabPanel key={mesFormatado} value={mesFormatado}>
            <MesInternacional
              mes={mes}
              imposto={impostoDoMes}
              resultado={resultadoDoMes}
            />
          </TabPanel>
        );
      })}
    </TabContext>
  );
}
